import { useState } from 'react';
import { FileDown, PackageCheck } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { Button } from '@siliconx/shared/ui/Button';
import type {
  EnvironmentalReportPackageFramework,
  EnvironmentalReportPackageManifest,
  EnvironmentalReportPackageSection,
} from '@siliconx/shared';
import { api, apiDownload, throwIfNotOk } from '../../lib/api';
import {
  DISCLOSURE_MAPPINGS,
  REPORT_PACKAGE_BUILD_BUTTON,
  REPORT_PACKAGE_COMPOSER_INTRO,
  REPORT_PACKAGE_COMPOSER_TITLE,
  REPORT_PACKAGE_CONTENTS_FOOTNOTE,
  REPORT_PACKAGE_EMPTY_SELECTION,
  REPORT_PACKAGE_PERIOD_LABEL,
  REPORT_PACKAGE_PERIOD_NOTE,
} from './reportsCopy';

/**
 * SIL-577 (I2-D5) — ESG Reports export-package composer, wayfinder
 * iteration 2 §7-D5. Pure composition UI: selects a subset of the SAME two
 * export files this tab already links to individually
 * (`AvailableReportsCard`'s fleet carbon export, `MethodologyVerificationCard`'s
 * methodology sheet), tagged with which disclosure framework(s) (the SAME
 * three `DISCLOSURE_MAPPINGS` rows the Disclosure mapping card renders) the
 * package is intended to support. The server (`POST
 * /api/client/environmental/reports/package`) returns a manifest naming
 * those selections — this component never downloads a bundled archive; each
 * manifest row downloads from its OWN existing, unchanged route (the exact
 * same `apiDownload` call `AvailableReportsCard`/`MethodologyVerificationCard`
 * already make), so package output is byte-for-byte identical to running
 * the individual exports (acceptance criterion #1).
 *
 * `period` has no interactive control — see
 * `dto/environmental-dashboard.ts`'s doc comment on why this route has
 * exactly one honest period today; rendered as a disabled, single-option
 * value (`REPORT_PACKAGE_PERIOD_LABEL`) rather than omitted, so the composer
 * still reads as "select framework(s), period, included sections" per the
 * ticket's scope, without implying a period picker this platform cannot
 * back.
 */
const SECTION_OPTIONS: { key: EnvironmentalReportPackageSection; label: string }[] = [
  { key: 'fleet_carbon_export', label: 'Fleet Carbon Export (CSV)' },
  { key: 'methodology_sheet', label: 'Methodology sheet (CSV)' },
];

export function ReportPackageComposerCard() {
  const [frameworks, setFrameworks] = useState<Set<EnvironmentalReportPackageFramework>>(
    () => new Set(DISCLOSURE_MAPPINGS.map((row) => row.key)),
  );
  const [sections, setSections] = useState<Set<EnvironmentalReportPackageSection>>(
    () => new Set(SECTION_OPTIONS.map((row) => row.key)),
  );

  const buildPackage = useMutation({
    mutationFn: async (): Promise<EnvironmentalReportPackageManifest> => {
      const res = await api.api.client.environmental.reports.package.$post({
        json: { intendedFrameworks: [...frameworks], sections: [...sections] },
      });
      await throwIfNotOk(res);
      return res.json();
    },
  });

  const toggle = <T,>(set: Set<T>, setSet: (next: Set<T>) => void, value: T) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setSet(next);
  };

  const canBuild = frameworks.size > 0 && sections.size > 0;

  return (
    <Card padding="md" className="flex flex-col gap-3">
      <SectionLabel>{REPORT_PACKAGE_COMPOSER_TITLE}</SectionLabel>
      <p className="text-xs leading-relaxed text-zinc-600">{REPORT_PACKAGE_COMPOSER_INTRO}</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
            Frameworks
          </span>
          {DISCLOSURE_MAPPINGS.map((row) => (
            <label
              key={row.key}
              className="flex cursor-pointer items-center gap-2 text-xs text-zinc-700"
            >
              <input
                type="checkbox"
                className="size-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                checked={frameworks.has(row.key)}
                onChange={() => toggle(frameworks, setFrameworks, row.key)}
              />
              {row.framework}
            </label>
          ))}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
            Sections
          </span>
          {SECTION_OPTIONS.map((row) => (
            <label
              key={row.key}
              className="flex cursor-pointer items-center gap-2 text-xs text-zinc-700"
            >
              <input
                type="checkbox"
                className="size-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                checked={sections.has(row.key)}
                onChange={() => toggle(sections, setSections, row.key)}
              />
              {row.label}
            </label>
          ))}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
            Period
          </span>
          <label
            className="flex items-center gap-2 text-xs text-zinc-500"
            title={REPORT_PACKAGE_PERIOD_NOTE}
          >
            <input type="checkbox" checked disabled className="size-4 rounded border-zinc-300" />
            {REPORT_PACKAGE_PERIOD_LABEL}
          </label>
        </div>
      </div>

      <div>
        <Button
          size="sm"
          disabled={!canBuild || buildPackage.isPending}
          onClick={() => buildPackage.mutate()}
        >
          <span className="inline-flex items-center gap-1.5">
            <PackageCheck className="size-3.5" />
            {REPORT_PACKAGE_BUILD_BUTTON}
          </span>
        </Button>
        {!canBuild && (
          <p className="mt-1.5 text-xs text-zinc-500">{REPORT_PACKAGE_EMPTY_SELECTION}</p>
        )}
        {buildPackage.isError && (
          <p className="mt-1.5 text-xs text-status-red">
            Could not build the package. Try again.
          </p>
        )}
      </div>

      {buildPackage.data && (
        <div className="mt-1 border-t border-zinc-200 pt-3">
          <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
            Package contents
          </span>
          <div className="mt-1.5 flex flex-col divide-y divide-zinc-100 rounded-md border border-zinc-200">
            {buildPackage.data.entries.map((entry) => (
              <div key={entry.section} className="flex items-center gap-3 p-3">
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm font-semibold text-zinc-800">{entry.name}</span>
                  <span className="font-mono text-[13px] text-zinc-500">
                    {entry.format}
                    {entry.asOfIso &&
                      ` · Data as of ${new Date(entry.asOfIso).toISOString().slice(0, 10)}`}
                  </span>
                </div>
                <StatusPill tone={entry.status === 'final' ? 'green' : 'amber'}>
                  {entry.status === 'final' ? 'Final' : 'Draft'}
                </StatusPill>
                <button
                  type="button"
                  onClick={() =>
                    void apiDownload(
                      entry.downloadPath,
                      `${entry.section}.${entry.format.toLowerCase()}`,
                    )
                  }
                  className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
                  aria-label={`Download ${entry.name}`}
                >
                  <FileDown className="size-3.5" />
                  Download
                </button>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-zinc-400">
            {REPORT_PACKAGE_CONTENTS_FOOTNOTE}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-zinc-400">
            {buildPackage.data.disclosure}
          </p>
        </div>
      )}
    </Card>
  );
}
